import * as turf from '@turf/turf';
import type { Polygon } from 'geojson';

import { getAlphaEarthTilesBaseUrl } from '@/config/env';
import type { AlphaEarthIndex } from '@/features/map/types/map.types';
import {
  buildAlphaEarthMetadataUrl,
  fetchAndProbeAlphaEarthTemplate,
} from '@/features/map/utils/alphaEarthTileMetadata';
import { polygonToAlphaEarthBboxCsv } from '@/features/map/utils/sarAoiGeometry';

export const SAR_PREVIEW_TILE_ZOOM = 11;

/**
 * Biên Web Mercator (độ) của ô tile XYZ tại zoom `z` — dùng neo ảnh preview Alpha Earth lên MapLibre (`image` source).
 */
export function webMercatorTileBoundsLngLat(
  x: number,
  y: number,
  z: number,
): { west: number; south: number; east: number; north: number } {
  const n = 2 ** z;
  const west = (x / n) * 360 - 180;
  const east = ((x + 1) / n) * 360 - 180;
  const north = tileYToLatRad(y, n);
  const south = tileYToLatRad(y + 1, n);
  return { west, south, east, north };
}

/**
 * Bốn góc [TL, TR, BR, BL] theo định dạng MapLibre cho `source type: image`.
 */
export function lngLatBboxToImageCoordinates(
  west: number,
  south: number,
  east: number,
  north: number,
): [[number, number], [number, number], [number, number], [number, number]] {
  return [
    [west, north],
    [east, north],
    [east, south],
    [west, south],
  ];
}

/**
 * Bốn góc [TL, TR, BR, BL] theo định dạng MapLibre cho `source type: image`.
 */
export function mercatorTileToImageCoordinates(
  x: number,
  y: number,
  z: number,
): [[number, number], [number, number], [number, number], [number, number]] {
  const b = webMercatorTileBoundsLngLat(x, y, z);
  return lngLatBboxToImageCoordinates(b.west, b.south, b.east, b.north);
}

function tileYToLatRad(yTile: number, n: number): number {
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * yTile) / n)));
  return (latRad * 180) / Math.PI;
}

function lngLatToTileXY(lng: number, lat: number, z: number): { x: number; y: number } {
  const n = 2 ** z;
  const x = Math.floor(((lng + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n);
  return { x, y };
}

/**
 * Bbox CSV + ô tile (z cố định) tại tâm AOI — dùng hydrate gallery SAR và refetch palette trong panel.
 */
export function resolveSarPreviewBboxAndTileXY(geom: Polygon | undefined): {
  bbox: string;
  x: number;
  y: number;
} | null {
  if (!geom) return null;
  const bbox = polygonToAlphaEarthBboxCsv(geom);
  if (!bbox) return null;

  let centerLng: number;
  let centerLat: number;
  try {
    const c = turf.center(turf.feature(geom));
    const coords = c.geometry.coordinates;
    centerLng = coords[0];
    centerLat = coords[1];
  } catch {
    const parts = bbox.split(',').map((s) => Number.parseFloat(s));
    if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return null;
    const [w, s, e, n] = parts;
    centerLng = (w + e) / 2;
    centerLat = (s + n) / 2;
  }

  const { x, y } = lngLatToTileXY(centerLng, centerLat, SAR_PREVIEW_TILE_ZOOM);
  return { bbox, x, y };
}

export interface AlphaEarthSarPreviewTemplateParams {
  bbox: string;
  date: string;
  index: AlphaEarthIndex;
  bands: string | null;
  palette: string | null;
}

export interface FetchAlphaEarthSarPreviewTileUrlParams extends AlphaEarthSarPreviewTemplateParams {
  x: number;
  y: number;
  /** Zoom áp dụng cho template. Mặc định `SAR_PREVIEW_TILE_ZOOM`. */
  z?: number;
}

/**
 * Cache template GEE **chỉ khi probe thành công** — lỗi tạm thời không ghi cache để lần sau gọi lại BE.
 * Dedupe Promise in-flight theo `(bbox|date|index|bands|palette)` — không gồm `z/x/y`.
 */
const sarPreviewTemplateCache = new Map<string, string>();
const sarPreviewTemplateInFlight = new Map<string, Promise<string | null>>();

function buildTemplateCacheKey(params: AlphaEarthSarPreviewTemplateParams): string {
  return [params.bbox, params.date, params.index, params.bands ?? '', params.palette ?? ''].join(
    '|',
  );
}

/** Thay `{z}/{x}/{y}` bằng giá trị cụ thể; an toàn khi template đã là URL đã gắn số. */
export function applyXyzToTileTemplate(template: string, z: number, x: number, y: number): string {
  return template.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
}

/**
 * Gọi BE metadata **không kèm z/x/y** để lấy template GEE (`.../tiles/{z}/{x}/{y}`),
 * probe một lần duy nhất qua `fetchAndProbeAlphaEarthTemplate`. Mọi caller cùng bộ tham số
 * (bbox,date,index,bands,palette) dùng chung kết quả — tránh gọi BE trùng cho từng ô.
 */
export async function fetchAlphaEarthSarPreviewTileTemplate(
  params: AlphaEarthSarPreviewTemplateParams,
): Promise<string | null> {
  const cacheKey = buildTemplateCacheKey(params);
  if (sarPreviewTemplateCache.has(cacheKey)) {
    return sarPreviewTemplateCache.get(cacheKey) ?? null;
  }
  const existing = sarPreviewTemplateInFlight.get(cacheKey);
  if (existing) return existing;

  const baseUrl = getAlphaEarthTilesBaseUrl();
  const metadataUrl = buildAlphaEarthMetadataUrl(baseUrl, {
    bbox: params.bbox,
    date: params.date,
    index: params.index,
    bands: params.bands,
    palette: params.palette,
  });

  const task = fetchAndProbeAlphaEarthTemplate(metadataUrl)
    .then((result) => {
      if (result.ok) {
        sarPreviewTemplateCache.set(cacheKey, result.template);
        return result.template;
      }
      return null;
    })
    .catch(() => null)
    .finally(() => {
      sarPreviewTemplateInFlight.delete(cacheKey);
    });

  sarPreviewTemplateInFlight.set(cacheKey, task);
  return task;
}

/**
 * Trả về URL ảnh của ô (x,y,z) cho preview Alpha Earth. Dưới capo reuse template cache —
 * không HEAD probe thêm vì template đã được probe `0/0/0` khi cache miss.
 */
export async function fetchAlphaEarthSarPreviewTileUrl(
  params: FetchAlphaEarthSarPreviewTileUrlParams,
): Promise<string | null> {
  const template = await fetchAlphaEarthSarPreviewTileTemplate({
    bbox: params.bbox,
    date: params.date,
    index: params.index,
    bands: params.bands,
    palette: params.palette,
  });
  if (!template) return null;
  const zoom = params.z ?? SAR_PREVIEW_TILE_ZOOM;
  return applyXyzToTileTemplate(template, zoom, params.x, params.y);
}

/** Xóa cache preview Alpha Earth (ví dụ khi đổi môi trường BE hoặc logout). */
export function clearAlphaEarthSarPreviewTileUrlCache(): void {
  sarPreviewTemplateCache.clear();
  sarPreviewTemplateInFlight.clear();
}
